"use client";

import { createContext, useCallback, useContext, useMemo, useRef, useState } from "react";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

type GuardCheckFn = () => boolean;

interface NavigationGuardContextValue {
  registerGuard: (id: string, check: GuardCheckFn) => void;
  unregisterGuard: (id: string) => void;
  /**
   * Call before programmatic navigation. Returns true if navigation is allowed.
   * Shows a confirmation dialog if any guard blocks.
   */
  confirmNavigation: () => boolean;
}

const NavigationGuardContext = createContext<NavigationGuardContextValue | null>(null);

export function useNavigationGuardContext(): NavigationGuardContextValue | null {
  return useContext(NavigationGuardContext);
}

interface NavigationGuardProviderProps {
  children: React.ReactNode;
}

/**
 * Provides in-app navigation blocking for forms with unsaved changes.
 *
 * Guards registered via `registerGuard` are checked synchronously before
 * any router.push / router.replace / Link navigation. If any guard's check
 * function returns false, navigation is blocked and a confirmation dialog
 * is shown.
 *
 * This provider also intercepts clicks on `<a>` elements within the app
 * that would trigger client-side navigation (Next.js Link).
 */
export function NavigationGuardProvider({ children }: NavigationGuardProviderProps) {
  const guardsRef = useRef<Map<string, GuardCheckFn>>(new Map());
  const [showDialog, setShowDialog] = useState(false);
  const pendingNavigationRef = useRef<(() => void) | null>(null);

  const registerGuard = useCallback((id: string, check: GuardCheckFn) => {
    guardsRef.current.set(id, check);
  }, []);

  const unregisterGuard = useCallback((id: string) => {
    guardsRef.current.delete(id);
  }, []);

  const confirmNavigation = useCallback((): boolean => {
    if (guardsRef.current.size === 0) return true;

    for (const check of guardsRef.current.values()) {
      if (!check()) {
        return false;
      }
    }
    return true;
  }, []);

  const handleConfirmLeave = useCallback(() => {
    // Temporarily clear all guards so navigation proceeds
    guardsRef.current.clear();
    setShowDialog(false);
    pendingNavigationRef.current?.();
    pendingNavigationRef.current = null;
  }, []);

  const handleCancelLeave = useCallback(() => {
    setShowDialog(false);
    pendingNavigationRef.current = null;
  }, []);

  const value = useMemo<NavigationGuardContextValue>(
    () => ({ registerGuard, unregisterGuard, confirmNavigation }),
    [registerGuard, unregisterGuard, confirmNavigation]
  );

  return (
    <NavigationGuardContext.Provider value={value}>
      {children}
      <UnsavedChangesDialog open={showDialog} onConfirm={handleConfirmLeave} onCancel={handleCancelLeave} />
    </NavigationGuardContext.Provider>
  );
}

// ---------------------------------------------------------------------------
// Unsaved Changes Dialog
// ---------------------------------------------------------------------------

interface UnsavedChangesDialogProps {
  open: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

function UnsavedChangesDialog({ open, onConfirm, onCancel }: UnsavedChangesDialogProps) {
  return (
    <AlertDialog open={open} onOpenChange={(isOpen) => !isOpen && onCancel()}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Unsaved Changes</AlertDialogTitle>
          <AlertDialogDescription>
            You have unsaved changes that will be lost if you leave this page. Are you sure you want to continue?
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={onCancel}>Stay on Page</AlertDialogCancel>
          <AlertDialogAction onClick={onConfirm}>Leave Page</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
